import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { useLocalPreviewVideoUrl } from '../../hooks/useLocalPreviewVideoUrl';
import {
  useDatoClient,
  useStore,
  useVideoSource,
} from '../../stores-v2/StoreContext';
import { useVideoCreatorStore } from '../../stores-v2/VideoCreatorStoreContext';
import { ContentTab } from '../../types.ts/general';
import {
  getLastShowcaseHasLanguages,
  recordLastShowcaseHasLanguages,
} from '../../utility/lastShowcaseHasLanguages';
import { recoverMissingStoryMetadata } from '../../utility/stableStoryMetadata';
import { parseClipThemeTitles } from '../../utility/story';
import { isCompressedRenditionPending } from '../../utility/storyReadiness';
import { shouldRekickTranscriptLoad } from '../../utility/transcriptLoadRetry';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { dismissContextualHint } from '../common/ContextualHint';
import { TOOLTIP_STYLE } from '../common/tooltipStyles';
import { CompositionProvider } from '../sidepanel/CompositionContext';
import BlogView from './BlogView';
import CaptionsContent from './CaptionsTab/CaptionsContent';
import EmailView from './EmailView';
import MediaView from './MediaView';
import PullQuotes from './QuoteCard/PullQuotes';
import SocialClipsView from './SocialClipsView';
import { TabIcons } from './TabIcons';
import {
  CONTAINED_TABS,
  contentTabLabel,
  parseContentTab,
  type ScrollMode,
} from './tabs';
import TalkingPointStudio from './TalkingPointStudio';
import TranscriptSpeakersContent from './TranscriptSpeakersTab/TranscriptSpeakersContent';
import {
  TRANSCRIPT_TAB_MOVED_HINT_KEY,
  TranscriptTabMovedHint,
} from './TranscriptTabMovedHint';
import { useShowcaseHasLanguages } from './useShowcaseHasLanguages';

import type { FC, ReactNode } from 'react';

type ContentViewProps = {
  videoSection?: ReactNode;
};

const getInitialTab = (searchParams: URLSearchParams): ContentTab => {
  return parseContentTab(searchParams.get('tab')) ?? ContentTab.video_clip;
};

// PLAT-11224 (audio-first): only Video Clips (clips/reels) genuinely needs the
// preview rendition, so it alone stays disabled until that lands — and even
// it opens early when this session still holds the uploaded file. Everything
// else (text products, Pull Quotes, Media, the transcript tabs) only needs the
// transcript and opens the moment it's ready.
// PLAT-11445: no tab is gated on video renditions any more. Video Clips opens
// at transcript-ready like the text tabs — its players show the session's
// local upload (or the processing placeholder) until the 270p lands, and clip
// cards resolve their media from the story at open. Kept as an array so a
// surface can be re-added if it ever truly needs a rendition to render.
const VIDEO_GATED_TABS: ContentTab[] = [];

const VIDEO_GATED_TAB_TOOLTIP_ID = 'video-gated-tab-tooltip';
/** Transcript-load retries the mid-pending poll may make per story (PLAT-11697). */
const MAX_POLL_TRANSCRIPT_REKICKS = 3;

const ContentView: FC<ContentViewProps> = observer(({ videoSection }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTabState, setActiveTab] = useState<ContentTab>(() =>
    getInitialTab(searchParams),
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pullQuotesWrapperRef = useRef<HTMLDivElement>(null);
  const { userIdentityStore, transcriptionStore } = useStore();
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const videoSourceStore = useVideoSource();
  const flags = useFlagsCombination(
    userIdentityStore.currentIdentity?.role as any,
  );
  // PLAT-11224 (audio-first): the story can open at transcription-ready with
  // no video rendition yet — video-dependent tabs wait for it. Video Clips
  // un-gates early when this session still holds the uploaded file: its
  // players can play the local video (PLAT-11228). Pull Quotes and Media
  // stay gated — they operate on pipeline artifacts, not a playable preview.
  const renditionPending = isCompressedRenditionPending(videoCreator.story);
  const localPreviewUrl = useLocalPreviewVideoUrl(videoCreator.story?.id);
  const isTabVideoGated = (tab: ContentTab) =>
    renditionPending &&
    VIDEO_GATED_TABS.includes(tab) &&
    !(tab === ContentTab.video_clip && localPreviewUrl);
  // Translation reviewers see only the Video Clips tab (their entry point to a
  // clip's transcript) and the Transcript & Speakers tab (reduced, read-only).
  // Any other `?tab=` value is coerced back to Video Clips.
  const reviewerMode = flags.enableTranslationReviewerMode;
  // PLAT-11991 — names the transcript tab for this showcase.
  const showcaseHasLanguages = useShowcaseHasLanguages();
  const REVIEWER_TABS: ContentTab[] = [
    ContentTab.video_clip,
    ContentTab.transcript_speakers,
  ];
  const activeTab = reviewerMode
    ? REVIEWER_TABS.includes(activeTabState)
      ? activeTabState
      : ContentTab.video_clip
    : activeTabState;
  // Legacy Content Studio transcript-editor tab has been retired (the
  // `src/transcript` module is removed). Kept as a constant so the existing
  // tab-gating logic below permanently hides/blocks the tab.
  const isTranscriptTabVisible = false;
  // `translationsEnabled` is hydrated by a follow-up fetch after the story
  // loads, so it is `undefined` for a beat on refresh. Hold the transcript
  // tab's label hidden until it resolves, so it never flips between
  // "Transcript" and "Transcript & Languages" in front of the user. A story with no showcase
  // never hydrates it, so that case resolves straight to the default label.
  // While it hydrates, the answer this browser last saw for the showcase
  // stands in, so only a first visit waits.
  const translationsEnabled = videoCreator.organization?.translationsEnabled;
  const showcaseId = videoCreator.organization?.id;
  const translationFieldsHydrating =
    translationsEnabled === undefined &&
    !(videoCreator.story && !videoCreator.organization);
  const rememberedHasLanguages = translationFieldsHydrating
    ? getLastShowcaseHasLanguages(showcaseId)
    : undefined;
  const transcriptTabLabelPending =
    translationFieldsHydrating && rememberedHasLanguages === undefined;
  const transcriptTabHasLanguages =
    rememberedHasLanguages ?? showcaseHasLanguages;
  useEffect(() => {
    if (!translationFieldsHydrating) {
      recordLastShowcaseHasLanguages(showcaseId, showcaseHasLanguages);
    }
  }, [translationFieldsHydrating, showcaseId, showcaseHasLanguages]);
  const [pendingScrollTab, setPendingScrollTab] = useState<ContentTab | null>(
    null,
  );

  // Tabs whose internal layout depends on a bounded parent (legacy
  // `flex: 1; min-height: 0; overflow: auto` chain). For these tabs we
  // re-bound `<Main>` to `100vh - header` so the chain works exactly like
  // before this branch — no document scroll, single inner scrollbar.
  const scrollMode = CONTAINED_TABS.has(activeTab) ? 'contained' : 'page';

  // Check for tab parameter in URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (!tabParam) return;

    // Find matching ContentTab value
    const matchingTab = Object.values(ContentTab).find(tab => tab === tabParam);
    if (!matchingTab) return;

    // For transcript editor tab, check if the feature flags are enabled
    // Don't check compatibility immediately as transcription might still be loading
    if (
      matchingTab === ContentTab.transcript_editor &&
      !isTranscriptTabVisible
    ) {
      return;
    }

    // For media tab, check if the feature flag is enabled
    if (
      matchingTab === ContentTab.media &&
      !flags.contentStudioMediaEnableTab
    ) {
      return;
    }

    // For talking points tab, check if the feature flag is enabled
    if (
      matchingTab === ContentTab.talking_point_studio &&
      !flags.contentStudioTalkingPointsEnableTab
    ) {
      return;
    }

    // For email tab, check if the feature flag is enabled
    if (
      matchingTab === ContentTab.email &&
      !flags.contentStudioEmailEnableTab
    ) {
      return;
    }

    // For transcript & speakers tab, check if the feature flag is enabled
    if (
      matchingTab === ContentTab.transcript_speakers &&
      !flags.contentStudioTranscriptSpeakersEnableTab
    ) {
      return;
    }

    // For captions tab, check if the feature flag is enabled
    if (
      matchingTab === ContentTab.captions &&
      !flags.contentStudioCaptionsEnableTab
    ) {
      return;
    }

    setActiveTab(matchingTab);
  }, [
    searchParams,
    isTranscriptTabVisible,
    flags.contentStudioMediaEnableTab,
    flags.contentStudioTalkingPointsEnableTab,
    flags.contentStudioEmailEnableTab,
    flags.contentStudioTranscriptSpeakersEnableTab,
    flags.contentStudioCaptionsEnableTab,
  ]);

  // Separate effect to handle compatibility check after transcription loads
  useEffect(() => {
    // If we're on the transcript editor tab but it's not compatible, redirect
    if (
      activeTab === ContentTab.transcript_editor &&
      isTranscriptTabVisible &&
      transcriptionStore.originalTranscription && // Only check after transcription is loaded
      !transcriptionStore.isTranscriptEditorCompatible
    ) {
      setActiveTab(ContentTab.video_clip);
      // Update URL to reflect the change
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', ContentTab.video_clip);
      setSearchParams(newParams);
    }
  }, [
    activeTab,
    isTranscriptTabVisible,
    transcriptionStore.originalTranscription,
    transcriptionStore.isTranscriptEditorCompatible,
    searchParams,
    setSearchParams,
  ]);

  // Handle scrolling when transcript data becomes ready
  useEffect(() => {
    if (pendingScrollTab && transcriptionStore.isTranscriptionReady) {
      // Transcript data is now ready, perform the scroll
      setTimeout(() => {
        if (pendingScrollTab === ContentTab.transcript_editor) {
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth',
          });
        }
        setPendingScrollTab(null);
      }, 100);
    }
  }, [pendingScrollTab, transcriptionStore.isTranscriptionReady]);

  // Handle scrolling when page loads directly on transcript tab
  useEffect(() => {
    // If we're on the transcript tab and transcript data becomes ready, scroll to bottom
    if (
      activeTab === ContentTab.transcript_editor &&
      transcriptionStore.isTranscriptionReady
    ) {
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth',
        });
      }, 200);
    }
  }, [activeTab, transcriptionStore.isTranscriptionReady]);

  // Scroll to bottom function for transcript tabs
  const scrollToBottom = () => {
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);
  };

  // Update URL when tab changes. `replace` is for redirects away from a tab the
  // user can't open, so Back doesn't return to it.
  const handleTabChange = (tab: ContentTab, { replace = false } = {}) => {
    // Prevent switching to video-dependent tabs while renditions are pending
    if (isTabVideoGated(tab)) {
      return;
    }

    // Prevent switching to Transcript Editor if the feature flags are disabled
    if (tab === ContentTab.transcript_editor && !isTranscriptTabVisible) {
      return;
    }

    // Prevent switching to Media tab if the feature flag is disabled
    if (tab === ContentTab.media && !flags.contentStudioMediaEnableTab) {
      return;
    }

    // Prevent switching to Talking Points tab if the feature flag is disabled
    if (
      tab === ContentTab.talking_point_studio &&
      !flags.contentStudioTalkingPointsEnableTab
    ) {
      return;
    }

    // Prevent switching to Email tab if the feature flag is disabled
    if (tab === ContentTab.email && !flags.contentStudioEmailEnableTab) {
      return;
    }

    // Prevent switching to Transcript & Speakers tab if the feature flag is disabled
    if (
      tab === ContentTab.transcript_speakers &&
      !flags.contentStudioTranscriptSpeakersEnableTab
    ) {
      return;
    }

    // Prevent switching to Captions tab if the feature flag is disabled
    if (tab === ContentTab.captions && !flags.contentStudioCaptionsEnableTab) {
      return;
    }

    // Handle scrolling for transcript-related tabs
    if (
      tab === ContentTab.transcript_editor ||
      tab === ContentTab.transcript_speakers
    ) {
      if (transcriptionStore.isTranscriptionReady) {
        // Transcript data is already loaded, scroll immediately
        scrollToBottom();
      } else {
        // Transcript data is not ready, set pending scroll
        setPendingScrollTab(tab);
      }
    } else if (
      tab === ContentTab.blog ||
      tab === ContentTab.social_posts ||
      tab === ContentTab.pull_quotes
    ) {
      // For content generation tabs, scroll to top of page to show tabs
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setActiveTab(tab);

    // Preserve existing parameters while updating the tab
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams, { replace });
  };

  // While renditions are pending, poll the story and patch the video fields
  // once they land — Content Studio has no other mid-session story refresh,
  // so without this the gated tabs, produce buttons, and the "Preview
  // Processing" badges would stay locked until a manual reload. Mirrors the
  // Creator Studio canvas-readiness poll (VideoCreator.tsx).
  const storyIdForPoll = videoCreator.story?.id;
  useEffect(() => {
    if (!renditionPending || !storyIdForPoll) return undefined;
    let cancelled = false;
    let pollInFlight = false;
    let transcriptRekicks = 0;
    const hydrateStory = async () => {
      if (pollInFlight) return;
      pollInFlight = true;
      try {
        const fresh =
          await datoClientStore.storyRepository?.findOne(storyIdForPoll);
        if (cancelled || !fresh) return;
        // The store may have switched stories while the fetch was in
        // flight — never patch a different story with this one's fields.
        const { story } = videoCreator;
        if (story?.id !== storyIdForPoll) return;
        const recoveredMetadata = recoverMissingStoryMetadata(
          story,
          fresh,
          parseClipThemeTitles,
        );
        // PLAT-11445: a story opened before its transcript existed never
        // learns about it — the producers (transcript-gated) and the
        // Transcript & Speakers tab would stay locked until a reload. Patch
        // the transcription fields as they land and kick the lazy load, the
        // way the Creator Studio readiness poll does.
        const freshTranscription = fresh.transcription;
        if (
          freshTranscription &&
          (!story.transcription?.elementsJson?.url ||
            !story.transcription?.audio?.url ||
            !story.transcription?.waveformData?.url)
        ) {
          runInAction(() => {
            story.transcription = freshTranscription;
          });
        }
        // PLAT-11697: a failed first load must be retried while renditions
        // are pending. TranscriptionStore.loadTranscription() marks ANY error
        // (a transient requestMissingFields miss, a Dato hiccup) as Failed and
        // nothing else in Content Studio re-kicks it; the Sidecar Path has all
        // three transcription URLs at open, so the missing-URL branch above
        // never re-kicked and the transcript tabs sat on "Preparing transcript
        // data…" until a Creator Studio round trip. loadTranscriptIfNeeded()
        // is a no-op when ready and waits when loading, so this is safe.
        // Bounded: a persistently failing load must not refetch (and flip the
        // tabs to their loading state) on every tick until renditions land.
        const { transcriptionStore: liveTranscription } =
          videoCreator.rootStore;
        if (
          transcriptRekicks < MAX_POLL_TRANSCRIPT_REKICKS &&
          shouldRekickTranscriptLoad({
            hasElementsUrl: !!freshTranscription?.elementsJson?.url,
            isReady: liveTranscription.isTranscriptionReady,
            isLoading: liveTranscription.isTranscriptionLoading,
          })
        ) {
          transcriptRekicks += 1;
          void videoCreator.loadTranscriptIfNeeded();
        }
        if (
          recoveredMetadata.aiResponse !== story.aiResponse ||
          recoveredMetadata.clipThemesJson !== story.clipThemesJson
        ) {
          runInAction(() => {
            story.aiResponse = recoveredMetadata.aiResponse;
            story.clipThemesJson = recoveredMetadata.clipThemesJson;
          });
        }
        if (!isCompressedRenditionPending(fresh)) {
          runInAction(() => {
            story.originalVideo = fresh.originalVideo;
            story.uploadLog = fresh.uploadLog;
          });
          // Story fields and the renderer's canonical source are separate
          // observability boundaries. Nudge an active clip editor only after
          // the fresh rendition is committed; this is a no-op when no
          // renderer is connected.
          videoSourceStore.refreshSource();
          clearInterval(poll);
          // Do not release the session-local File here. Backend readiness can
          // flip before the rendition bytes are fetchable, and this component
          // may unmount for Creator Studio immediately after the poll. The
          // active editor owns release after it has successfully painted the
          // processed rendition; dropping it here races that hand-off and
          // leaves Creator with neither source.
        }
      } catch {
        // Transient fetch failure — next tick retries.
      } finally {
        pollInFlight = false;
      }
    };
    void hydrateStory();
    const poll = setInterval(hydrateStory, 5000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renditionPending, storyIdForPoll]);

  // Landing on (or deep-linking to) a video-gated tab while renditions are
  // pending bounces to Transcript & Speakers — the surface built for
  // audio-first arrival. Tabs re-enable reactively once the rendition lands.
  useEffect(() => {
    if (isTabVideoGated(activeTab)) {
      setActiveTab(ContentTab.transcript_speakers);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', ContentTab.transcript_speakers);
      setSearchParams(newParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renditionPending, activeTab, localPreviewUrl]);

  return (
    <Main data-testid="content-view" $scrollMode={scrollMode}>
      <Tabs
        id="content-tabs-nav"
        data-testid="content-tab-bar"
        $scrollMode={scrollMode}
      >
        {Object.entries(ContentTab).map(([k, v]) => {
          // Translation reviewers get only Video Clips + Transcript & Speakers.
          if (reviewerMode && !REVIEWER_TABS.includes(v)) {
            return null;
          }
          // Skip the Transcript Editor tab if the feature flags are disabled
          if (v === ContentTab.transcript_editor && !isTranscriptTabVisible) {
            return null;
          }
          // Skip the Media tab if the feature flag is disabled
          if (v === ContentTab.media && !flags.contentStudioMediaEnableTab) {
            return null;
          }
          // Skip the Talking Points tab if the feature flag is disabled
          if (
            v === ContentTab.talking_point_studio &&
            !flags.contentStudioTalkingPointsEnableTab
          ) {
            return null;
          }
          // Skip the Email tab if the feature flag is disabled
          if (v === ContentTab.email && !flags.contentStudioEmailEnableTab) {
            return null;
          }
          // Skip the Transcript & Speakers tab if the feature flag is disabled
          if (
            v === ContentTab.transcript_speakers &&
            !flags.contentStudioTranscriptSpeakersEnableTab
          ) {
            return null;
          }
          // Skip the Captions tab if the feature flag is disabled
          if (
            v === ContentTab.captions &&
            !flags.contentStudioCaptionsEnableTab
          ) {
            return null;
          }
          const videoGated = isTabVideoGated(v);
          const movedTab = v === ContentTab.transcript_speakers;
          const tab = (
            <TabItem
              key={k}
              isSelected={activeTab === v}
              $videoGated={videoGated}
              onClick={() => {
                // PLAT-11980 — clicking the moved tab is finding it: the
                // coach mark under it has done its job.
                if (movedTab)
                  dismissContextualHint(TRANSCRIPT_TAB_MOVED_HINT_KEY);
                handleTabChange(v);
              }}
              data-tooltip-id={
                videoGated ? VIDEO_GATED_TAB_TOOLTIP_ID : undefined
              }
              data-tooltip-content={
                videoGated ? 'Video is still processing' : undefined
              }
            >
              {TabIcons[v]}
              {v === ContentTab.transcript_speakers &&
              transcriptTabLabelPending ? (
                // Reserves the label's width so the tabs after it don't shift.
                <PendingTabLabel aria-hidden>{v}</PendingTabLabel>
              ) : (
                contentTabLabel(v, transcriptTabHasLanguages)
              )}
            </TabItem>
          );
          // PLAT-11980 — the tab moved here from the end of the bar; say so
          // once per person. Reviewers' two-tab bar never had it elsewhere.
          // Held while the label is pending so it never points at a blank tab.
          return movedTab ? (
            <TranscriptTabMovedHint
              key={k}
              show={!reviewerMode && !transcriptTabLabelPending}
              hasLanguages={transcriptTabHasLanguages}
            >
              {tab}
            </TranscriptTabMovedHint>
          ) : (
            tab
          );
        })}
        <Tooltip
          id={VIDEO_GATED_TAB_TOOLTIP_ID}
          place="bottom"
          style={TOOLTIP_STYLE}
        />
        {/* {(activeTab === ContentTab.blog || activeTab === ContentTab.email) && (
          <div className="share">
            <ContentShare />
          </div>
        )} */}
      </Tabs>

      {(() => {
        switch (activeTab) {
          case ContentTab.video_clip:
            return (
              <ContentWrapper
                ref={wrapperRef}
                $topPadding="30px"
                $scrollMode="page"
              >
                {videoSection}
                <CompositionProvider>
                  <SocialClipsView />
                </CompositionProvider>
              </ContentWrapper>
            );
          case ContentTab.blog:
          case ContentTab.social_posts:
            // Rendered below as always-mounted (like PullQuotes)
            return null;
          case ContentTab.talking_point_studio:
            if (!flags.contentStudioTalkingPointsEnableTab) {
              setTimeout(
                () => handleTabChange(ContentTab.video_clip, { replace: true }),
                0,
              );
              return null;
            }
            return (
              <ContentWrapper ref={wrapperRef} $scrollMode="contained">
                <TalkingPointStudio />
              </ContentWrapper>
            );
          case ContentTab.media:
            if (!flags.contentStudioMediaEnableTab) {
              setTimeout(
                () => handleTabChange(ContentTab.video_clip, { replace: true }),
                0,
              );
              return null;
            }
            return (
              <ContentWrapper ref={wrapperRef} $scrollMode="contained">
                <MediaView />
              </ContentWrapper>
            );
          case ContentTab.pull_quotes:
            // PullQuotes are handled separately below to stay mounted
            return null;
          case ContentTab.email:
            if (!flags.contentStudioEmailEnableTab) {
              setTimeout(
                () => handleTabChange(ContentTab.video_clip, { replace: true }),
                0,
              );
              return null;
            }
            // Rendered below as always-mounted (like PullQuotes)
            return null;
          case ContentTab.transcript_speakers:
            if (!flags.contentStudioTranscriptSpeakersEnableTab) {
              setTimeout(
                () => handleTabChange(ContentTab.video_clip, { replace: true }),
                0,
              );
              return null;
            }
            // Rendered below as always-mounted (like PullQuotes)
            return null;
          case ContentTab.captions:
            if (!flags.contentStudioCaptionsEnableTab) {
              setTimeout(
                () => handleTabChange(ContentTab.video_clip, { replace: true }),
                0,
              );
              return null;
            }
            // Rendered below as always-mounted to avoid slow remount
            return null;
          default:
            return (
              <ContentWrapper
                ref={wrapperRef}
                $scrollMode="contained"
              ></ContentWrapper>
            );
        }
      })()}

      {/* Keep PullQuotes mounted to prevent renderer disposal */}
      <PullQuotesWrapper
        ref={pullQuotesWrapperRef}
        $isVisible={activeTab === ContentTab.pull_quotes}
        $scrollMode="page"
      >
        <PullQuotes />
      </PullQuotesWrapper>

      {/* Keep BlogView mounted to preserve editing state across tab switches */}
      <BlogViewWrapper
        $isVisible={activeTab === ContentTab.blog}
        $scrollMode="contained"
      >
        <BlogView key="blog" type="blog" />
      </BlogViewWrapper>

      <BlogViewWrapper
        $isVisible={activeTab === ContentTab.social_posts}
        $scrollMode="contained"
      >
        <BlogView key="socialPost" type="socialPost" />
      </BlogViewWrapper>

      {/* Keep EmailView mounted to preserve editing state across tab switches */}
      {flags.contentStudioEmailEnableTab && (
        <BlogViewWrapper
          $isVisible={activeTab === ContentTab.email}
          $scrollMode="page"
        >
          <EmailView />
        </BlogViewWrapper>
      )}

      {/* Keep TranscriptSpeakersContent mounted to avoid slow remount (10s+ DatoCMS fetch) */}
      {flags.contentStudioTranscriptSpeakersEnableTab && (
        <KeepMountedWrapper
          $isVisible={activeTab === ContentTab.transcript_speakers}
          $scrollMode="contained"
        >
          <TranscriptSpeakersContent
            active={activeTab === ContentTab.transcript_speakers}
          />
        </KeepMountedWrapper>
      )}

      {/* Keep CaptionsContent mounted to avoid slow remount (PLAT-9626) */}
      {flags.contentStudioCaptionsEnableTab && (
        <KeepMountedWrapper $isVisible={activeTab === ContentTab.captions}>
          <CaptionsContent />
        </KeepMountedWrapper>
      )}
    </Main>
  );
});

export default ContentView;

const Main = styled.div<{ $scrollMode: ScrollMode }>`
  display: flex;
  flex-direction: column;
  ${props =>
    props.$scrollMode === 'contained' &&
    css`
      /* Pre-branch CSS for tabs whose internal layout depends on this Main
         being height-bounded (Talking Points, Media, Transcript Editor,
         Transcript & Speakers). ContentStudio's outer Main is also bounded
         (height: 100vh; overflow: hidden) for these tabs, so flex:1 chains
         resolve exactly as before this branch. */
      flex: 1;
      min-height: 0;
      overflow: hidden;
    `}
`;
const PendingTabLabel = styled.span`
  visibility: hidden;
`;

const Tabs = styled.nav<{ $scrollMode: ScrollMode }>`
  flex-shrink: 0;
  z-index: 10;
  background: var(--background);
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 0 var(--spacing-lg);
  align-items: center;
  border-bottom: 1px solid var(--border-subtle);
  padding: var(--spacing-sm) var(--spacing-sm) 0;
  ${props =>
    props.$scrollMode === 'page' &&
    css`
      /* Only stick on page-mode tabs (Video Clip, Pull Quotes, Articles,
         Social Posts, Email). For contained-mode tabs, ContentView's <Main>
         has overflow: hidden, which would make sticky's \`top\` value act as
         a minimum offset — pushing the tab bar down by --content-studio-
         header-h pixels even though it never scrolls. */
      position: sticky;
      top: var(--content-studio-header-h, 0px);
    `}
`;
const TabItem = styled.menu<{ isSelected?: boolean; $videoGated?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: var(--spacing-sm) 0;
  margin: 0;
  cursor: ${props => (props.$videoGated ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$videoGated ? 0.45 : 1)};
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  transition: color var(--transition-fast);
  border: none;
  background: none;

  svg {
    flex-shrink: 0;
    opacity: 0.6;
  }

  &:hover {
    color: var(--foreground);

    svg {
      opacity: 1;
    }
  }

  ${props =>
    props.$videoGated &&
    css`
      &:hover {
        color: var(--text-secondary);

        svg {
          opacity: 0.6;
        }
      }
    `}

  ${props =>
    props.isSelected &&
    css`
      color: var(--foreground);
      font-weight: var(--font-weight-medium);

      svg {
        opacity: 1;
      }

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: var(--spacing-2xs);
        background-color: var(--accent);
        border-radius: 1px;
      }
    `}
`;

// `page` lets the wrapper flow naturally so the document scroll is the only
// scrollbar (sticky header + tabs stay pinned at top). `contained` is the
// legacy `flex:1; min-height:0; overflow:auto` chain — used for tabs whose
// inner layout (e.g. Transcript & Speakers' 3-column absolute positioning,
// Transcript Editor) depends on a bounded parent. It only works because
// `Main` is itself height-bounded for those tabs (`$scrollMode` prop).
const containedScrollStyles = css`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const ContentWrapper = styled.div<{
  $topPadding?: string;
  $scrollMode?: ScrollMode;
}>`
  display: flex;
  flex-direction: column;
  padding-top: ${props => props.$topPadding || 'var(--spacing-sm)'};
  ${props => props.$scrollMode === 'contained' && containedScrollStyles}
`;

/** Keeps a tab mounted in the DOM, toggling visibility via display. */
const KeepMountedWrapper = styled.div<{
  $isVisible: boolean;
  $scrollMode?: ScrollMode;
}>`
  display: ${props => (props.$isVisible ? 'flex' : 'none')};
  flex-direction: column;
  padding-top: 30px;
  padding-bottom: var(--spacing-lg);
  ${props => props.$scrollMode === 'contained' && containedScrollStyles}
`;

const PullQuotesWrapper = styled(KeepMountedWrapper)`
  padding-top: var(--spacing-sm);
  margin-bottom: 30px;
`;

const BlogViewWrapper = styled(KeepMountedWrapper)`
  padding-top: var(--spacing-sm);
`;
